import { useEffect } from "react";
import { useRouter } from "next/router";

type JsonSchema = {
  type: string;
  properties?: Record<string, unknown>;
  required?: string[];
  additionalProperties?: boolean;
};

type WebMcpTool = {
  name: string;
  description: string;
  inputSchema: JsonSchema;
  execute: (input: Record<string, unknown>) => Promise<unknown> | unknown;
};

type ModelContext = {
  provideContext?: (context: { tools: WebMcpTool[] }) => void;
  registerTool?: (tool: WebMcpTool) => void | (() => void);
};

declare global {
  interface Navigator {
    modelContext?: ModelContext;
  }
}

/**
 * Exposes site actions to in-browser AI agents via WebMCP (navigator.modelContext).
 * provideContext is the primary API; registerTool is used where only it exists.
 */
export default function WebMcpTools() {
  const router = useRouter();

  useEffect(() => {
    const modelContext = navigator.modelContext;
    if (!modelContext) return;

    const tools: WebMcpTool[] = [
      {
        name: "navigate_to_path",
        description:
          "Navigate the brand website to a path (e.g. /, /wines, /contacts).",
        inputSchema: {
          type: "object",
          properties: {
            path: {
              type: "string",
              description: "Site-relative path starting with /",
            },
          },
          required: ["path"],
          additionalProperties: false,
        },
        execute: async (input) => {
          const path = String(input.path || "/");
          if (!path.startsWith("/") || path.startsWith("//")) {
            return { ok: false, error: "path must be a site-relative path" };
          }
          await router.push(path);
          return { ok: true, path };
        },
      },
      {
        name: "get_page_context",
        description:
          "Return the current page URL, title, locale, and pathname for agent context.",
        inputSchema: {
          type: "object",
          properties: {},
          additionalProperties: false,
        },
        execute: async () => ({
          url: window.location.href,
          pathname: window.location.pathname,
          title: document.title,
          locale: router.locale || null,
          asPath: router.asPath,
        }),
      },
      {
        name: "list_nav_links",
        description:
          "List primary navigation anchor links currently rendered on the page.",
        inputSchema: {
          type: "object",
          properties: {},
          additionalProperties: false,
        },
        execute: async () => {
          const anchors = Array.from(
            document.querySelectorAll(
              "header a[href], nav a[href]"
            ) as NodeListOf<HTMLAnchorElement>
          );
          const links = anchors
            .map((a) => ({
              href: a.getAttribute("href") || "",
              text: (a.textContent || "").replace(/\s+/g, " ").trim(),
            }))
            .filter((l) => l.href && l.text)
            .slice(0, 40);
          return { links };
        },
      },
    ];

    const cleanups: Array<() => void> = [];

    if (typeof modelContext.provideContext === "function") {
      try {
        modelContext.provideContext({ tools });
        cleanups.push(() => modelContext.provideContext?.({ tools: [] }));
      } catch (error) {
        console.warn("[WebMCP] provideContext failed", error);
      }
    } else if (typeof modelContext.registerTool === "function") {
      tools.forEach((tool) => {
        try {
          const maybeCleanup = modelContext.registerTool?.(tool);
          if (typeof maybeCleanup === "function") {
            cleanups.push(maybeCleanup);
          }
        } catch (error) {
          console.warn("[WebMCP] registerTool failed", tool.name, error);
        }
      });
    }

    return () => {
      cleanups.forEach((fn) => {
        try {
          fn();
        } catch {
          // ignore unregister errors
        }
      });
    };
  }, [router]);

  return null;
}
